import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { instance } from "api/apiBase";
import { useEffect, useState } from "react";
import { Applicant } from "types/application";
import {
  BankStatementTransaction,
  OrderBy,
  OrderDirection,
  TransactionsResponse
} from "../types";
import {
  buildTxUrl,
  INIT_SORTING,
  ITEMS_PER_PAGE,
  serializeParams
} from "../utils";

type Params = {
  applicationId: number;
  verificationId: number;
  applicant: Applicant;
  incomeId?: number;
  selectionIds: number[];
  enabled: boolean;
};

export const useConfirmationViewController = ({
  applicationId,
  verificationId,
  applicant,
  incomeId,
  selectionIds,
  enabled
}: Params) => {
  const [page, setPage] = useState(1);
  const [sortingState, setSortingState] = useState<SortingState>(INIT_SORTING);

  const orderState = sortingState[0] ?? INIT_SORTING[0];
  const orderBy = orderState.id as OrderBy;
  const orderDirection: OrderDirection = orderState.desc ? "desc" : "asc";

  const selectionIdsKey = selectionIds.join(",");

  useEffect(() => {
    setPage(1);
  }, [orderBy, orderDirection, selectionIdsKey]);

  const query = useQuery({
    queryKey: [
      "APPLICATION_BANK_STATEMENT_TRANSACTIONS_CONFIRMATION",
      applicationId,
      verificationId,
      applicant.id,
      incomeId,
      page,
      ITEMS_PER_PAGE,
      orderBy,
      orderDirection,
      selectionIdsKey
    ],
    queryFn: () =>
      instance
        .get<TransactionsResponse>(buildTxUrl(applicationId, verificationId), {
          params: {
            page,
            per_page: ITEMS_PER_PAGE,
            order_by: orderBy,
            order_direction: orderDirection,
            applicant_id: applicant.id,
            income_id: incomeId,
            item_ids: selectionIds
          },
          paramsSerializer: serializeParams
        })
        .then(({ data }) => data),
    enabled,
    placeholderData: keepPreviousData
  });

  const items: BankStatementTransaction[] = query.data?.items ?? [];
  const totalPages = query.data?.page_count ?? 1;

  return {
    items,
    totalPages,
    isLoading: query.isLoading,
    isEmpty: !query.isLoading && items.length === 0,
    page,
    setPage,
    sortingState,
    setSortingState
  };
};
